// Logic tài chính theo docs/ref/COST-LOGIC.md (chốt 26/Oct): bản chất, GP, markup, tuổi nợ, hạn mức. Hàm thuần, tiền = triệu ₫.
import type {
  ArCustomer, CostLine, CustomerBears, DebitLine, DocKind, DocOwner, Nature, Payer, PayRisk, SupplierPartner, SupplierPayment, Voucher,
} from './types';

/** Mã bản chất từ câu 1 (chứng từ tên ai) × câu 2 (khách có chịu). `unknown` → chưa rõ, chuyển Sale quyết. */
export function natureOf(owner: DocOwner, bears: CustomerBears): Nature | null {
  if (bears === 'unknown') return null;
  if (owner === 'customer') return bears === 'yes' ? 'OBH' : 'EXC';
  return bears === 'yes' ? 'BIL' : 'ABS';
}

/** OBH chỉ đi với chứng từ tên khách; không chứng từ (NDC) không thể OBH. Trả lý do vi phạm hoặc null. */
export function natureViolation(line: Pick<CostLine, 'nature' | 'doc' | 'docOwner' | 'source' | 'reason'>): string | null {
  if (line.nature === 'OBH' && (line.doc === 'NDC' || line.docOwner !== 'customer')) return 'OBH chỉ đi với chứng từ tên khách';
  if (line.source === 'ops' && !line.reason?.trim()) return 'OPS sửa khỏi mặc định phải ghi lý do';
  return null;
}

/** Chi hộ mà chứng từ tên HANA → cờ đỏ (chuyển BIL hoặc xuất lại tên khách). */
export function isOnBehalfWrongName(line: Pick<CostLine, 'nature' | 'docOwner' | 'doc'>): boolean {
  return line.nature === 'OBH' && line.doc !== 'NDC' && line.docOwner === 'hana';
}

/** Sang ABS / EXC thì cần duyệt. */
export const needsApproval = (n: Nature): boolean => n === 'ABS' || n === 'EXC';

/** Gợi ý làm sạch: biên lai (cá nhân) → bảng kê lên RCP; doanh nghiệp → yêu cầu HĐ lên INV. */
export function cleanupHint(doc: DocKind, recipient: 'person' | 'business'): DocKind | null {
  if (doc === 'NDC') return recipient === 'person' ? 'RCP' : 'INV';
  if (doc === 'PND') return 'INV';
  return null;
}

export const sum = (a: readonly number[]): number => Math.round(a.reduce((x, y) => x + y, 0) * 1000) / 1000;

/** Tổng theo bản chất. */
export function totalsByNature(lines: readonly Pick<CostLine, 'nature' | 'amount'>[]): Record<Nature, number> {
  const t: Record<Nature, number> = { OBH: 0, BIL: 0, ABS: 0, EXC: 0 };
  for (const l of lines) t[l.nature] = sum([t[l.nature], l.amount]);
  return t;
}

/** GP = REV − (BIL + ABS); không tính OBH, COL (và EXC, chưa vào sổ cho tới khi KTT duyệt). */
export function grossProfit(revenue: number, lines: readonly Pick<CostLine, 'nature' | 'amount'>[]): number {
  const t = totalsByNature(lines);
  return sum([revenue, -t.BIL, -t.ABS]);
}

/** Phiếu: tổng dòng = hoá đơn. Công ty trả và nhân viên trả phải tách phiếu. */
export function splitMixedPayers(lines: readonly Pick<CostLine, 'voucher' | 'payer'>[]): string[] {
  const seen = new Map<string, Set<Payer>>();
  for (const l of lines) seen.set(l.voucher, (seen.get(l.voucher) ?? new Set()).add(l.payer));
  return [...seen].filter(([, p]) => p.size > 1).map(([v]) => v);
}

/** Giá debit = giá gốc + markup (số tiền hoặc %). OBH khoá markup: giá debit = đúng chứng từ. */
export function debitPrice(l: Pick<DebitLine, 'nature' | 'base' | 'markup'>): number {
  if (l.nature === 'OBH' || l.nature === 'ABS' || l.nature === 'EXC') return l.base;
  const add = l.markup.kind === 'percent' ? (l.base * l.markup.value) / 100 : l.markup.value;
  return Math.round((l.base + add) * 1000) / 1000;
}

export const markupAmount = (l: Pick<DebitLine, 'nature' | 'base' | 'markup'>): number => sum([debitPrice(l), -l.base]);

/** Chỉ dòng BIL / REV được markup. */
export const canMarkup = (n: DebitLine['nature']): boolean => n === 'BIL' || n === 'REV';

/** Vai trò xem cột Giá gốc / Markup ("Xem markup"): Sale / CS / KT / BOD thấy, OPS không. */
export type MarkupRole = 'SALE' | 'CS' | 'KT' | 'BOD' | 'OPS';
export const canSeeMarkup = (r: MarkupRole): boolean => r !== 'OPS';

/** Debit gửi khách chỉ có giá debit — bỏ giá gốc / markup. */
export function customerDebit(lines: readonly DebitLine[]): { id: string; code: string; label: string; price: number }[] {
  return lines.map((l) => ({ id: l.id, code: l.code, label: l.label, price: debitPrice(l) }));
}

/** Tuổi nợ của khách: tổng nợ, quá hạn, chặn release (nợ >90 ngày hoặc vượt hạn mức), giữ DO (nợ 31–90). */
export function arSummary(c: ArCustomer): { over: number; total: number; block: boolean; hold: boolean; flag: '' | 'Chặn release' | 'Giữ DO' } {
  const over = sum([c.d1_30, c.d31_60, c.d61_90, c.d90]);
  const total = sum([c.current, over]);
  const block = c.d90 > 0 || total > c.limit;
  const hold = !block && c.d31_60 + c.d61_90 > 0;
  return { over, total, block, hold, flag: block ? 'Chặn release' : hold ? 'Giữ DO' : '' };
}

/** Rủi ro khi chi cho đối tác (board Mint-SupplierPay): `block` chặn chi, `warn` cảnh báo. */
export function paymentRisks(p: Pick<SupplierPayment, 'extraRisk' | 'docs'>, partner: Pick<SupplierPartner, 'bank'> | undefined): { k: string; level: 'block' | 'warn' }[] {
  const out: { k: string; level: 'block' | 'warn' }[] = [];
  const bank = partner?.bank ?? 'ok';
  if (bank === 'locked') out.push({ k: 'Đối tác tạm khoá chi', level: 'block' });
  if (bank === 'changed') out.push({ k: 'TK nhận vừa đổi', level: 'block' });
  if (bank === 'unverified') out.push({ k: 'TK chưa xác minh', level: 'block' });
  if (bank === 'shared') out.push({ k: 'TK dùng chung đối tác khác', level: 'warn' });
  const ex: Record<PayRisk, [string, 'block' | 'warn']> = { over: ['Vượt tổng chứng từ', 'block'], dup: ['Trùng phiếu chi', 'block'], mix: ['Trộn deposit với cước', 'warn'] };
  for (const r of p.extraRisk) out.push({ k: ex[r][0], level: ex[r][1] });
  if (!p.docs.length) out.push({ k: 'Chưa link chứng từ', level: 'block' });
  return out;
}

/** Số bước duyệt của phiếu: cấp 1 → kế toán, chi ≥ 200 tr thêm ACC-CA. */
export const ACC_CA_THRESHOLD = 200;
export const approvalSteps = (v: Pick<Voucher, 'total'>): ('c1' | 'kt' | 'gd')[] => (v.total > ACC_CA_THRESHOLD ? ['c1', 'kt', 'gd'] : ['c1', 'kt']);

/** Tổng tạm ứng còn giữ của một người. */
export const advanceHeld = (a: readonly { who: string; held: number; status: string }[], who: string): number =>
  sum(a.filter((x) => x.who === who && x.status !== 'done').map((x) => x.held));
